import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ChevronRight, ExternalLink, MapPin, Check, Share2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

const HERO_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/kYQbBouhSHqeCLzh.webp";
const OG_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/xPstcpmWUtIaQwFY.webp";

const WHATTODO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp";
const COSTOFLIVING_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ENTERTAINMENT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-piata-sfatului-night_37c559b4.webp";

const RELATED = [
  { tag: "City Guide", title: "What to Do in Brașov: The Complete City Guide for Expats", img: WHATTODO_IMG, href: "/what-to-do-in-brasov-city-guide" },
  { tag: "Relocation", title: "Cost of Living in Brașov 2026: Real Numbers for Expats", img: COSTOFLIVING_IMG, href: "/cost-of-living-brasov" },
  { tag: "City Guide", title: "Entertainment in Brașov: Day & Night Guide for Adults", img: ENTERTAINMENT_IMG, href: "/entertainment-brasov-guide" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60"
        style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
      >
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
          alt="Cartier Transilvania Brașov"
          className="w-full h-48 object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
        >
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
          alt="Cartier Transilvania"
          className="w-full md:w-56 h-40 object-cover flex-shrink-0"
          loading="lazy"
        />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
          >
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleBrasovBusGuide() {
  return (
    <>
      <Helmet>
        <title>Bus No. 24 Brașov & 4 More Routes Worth Taking — Public Transport Guide</title>
        <meta name="description" content="Bus no. 24, 20, 55, 50 and 100 in Brașov — routes, stops, ticket prices and practical tips for tourists and expats. The cheapest way to explore the city." />
        <meta property="og:title" content="Bus No. 24 Brașov & 4 More Routes Worth Taking — Public Transport Guide" />
        <meta property="og:description" content="Bus no. 24, 20, 55, 50 and 100 in Brașov — routes, stops, ticket prices and practical tips for tourists and expats. The cheapest way to explore the city." />
        <meta property="og:image" content={OG_IMG} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Bus no. 24 Brașov — RATBV electric bus on Strada Lungă" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://brasovgateway.com/brasov-bus-guide" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bus No. 24 Brașov & 4 More Routes Worth Taking — Public Transport Guide" />
        <meta name="twitter:description" content="Bus no. 24, 20, 55, 50 and 100 in Brașov — routes, stops, ticket prices and practical tips for tourists and expats. The cheapest way to explore the city." />
        <meta name="twitter:image" content={OG_IMG} />
        <meta name="keywords" content="bus no 24 Brasov, RATBV Brasov, Brasov bus routes, bus Poiana Brasov, public transport Brasov, linia 24 Brasov" />
        <link rel="canonical" href="https://brasovgateway.com/brasov-bus-guide" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "@id": "https://brasovgateway.com/brasov-bus-guide#article",
                    "headline": "Bus no. 24 Brașov and 4 more routes worth taking — what you will see along the way",
                "description": "Bus no. 24, 20, 55, 50 and 100 in Brașov — routes, stops, ticket prices and practical tips for tourists and expats. The cheapest way to explore the city.",
                "image": "${OG_IMG}",
                "datePublished": "2026-04-28",
                "dateModified": "2026-04-28",
                "author": {
                  "@type": "Organization",
                  "name": "Brașov Gateway",
                  "url": "https://brasovgateway.com"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Brașov Gateway",
                  "url": "https://brasovgateway.com"
                },
                "url": "https://brasovgateway.com/brasov-bus-guide",
                "articleSection": "City Guide"
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://brasovgateway.com/brasov-bus-guide#breadcrumb",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://brasovgateway.com" },
                  { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://brasovgateway.com/blog" },
                  { "@type": "ListItem", "position": 3, "name": "Brașov by Bus: 5 Routes Worth Taking", "item": "https://brasovgateway.com/brasov-bus-guide" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
        <SiteHeader />

        {/* Hero */}
        <div className="relative h-72 md:h-96 mt-16">
          <img
            src={HERO_IMG}
            alt="Bus no. 24 Brașov — RATBV electric bus on Strada Lungă"
            className="w-full h-full object-cover"
            fetchPriority="high"
            width="1600"
            height="900"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
        </div>

        <div className="container py-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main content */}
            <main className="flex-1 min-w-0">
              <article className="max-w-none">
                <header className="mb-10">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-xs font-bold uppercase tracking-widest px-3 py-1" style={{ backgroundColor: "oklch(0.38 0.12 15)", color: "white" }}>
                      City Guide
                    </span>
                    <span className="text-xs text-stone-500 uppercase tracking-widest">April 2026</span>
                  </div>
                  <h1 className="text-stone-900 leading-tight mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800 }}>
                    Bus no. 24 Brașov and 4 more routes worth taking — what you will see along the way
                  </h1>
                  <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        <img
                          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
                          alt="Brașov Gateway"
                          className="w-full h-full object-cover opacity-50"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                        <p className="text-xs text-stone-500">City Guide Team</p>
                      </div>
                    </div>
                    <ShareButton url="https://brasovgateway.com/brasov-bus-guide" title="Brașov by Bus: 5 Routes Worth Taking — Public Transport Guide 2026" />
                  </div>
                </header>

                {/* Lead paragraph */}
                <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed mt-6 mb-8" style={{ lineHeight: "1.6" }}>
                  Bus no. 24 Brașov is the cheapest way to see the city — and also one of the most underrated. A single bus ticket costs 5 lei — roughly one euro — and some of the routes take you places that no walking tour will. The problem is that nobody talks about them, so most visitors spend their entire stay within a 10-minute walk of Piața Sfatului, never knowing that the city extends quite a bit further. If you are looking for{" "}
                  <Link href="/what-to-do-in-brasov-city-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    things to do in Brașov
                  </Link>
                  {" "}that go beyond the old town, the bus is where you start.
                </p>

                {/* Quick Facts */}
                <div className="my-10 p-6 border-l-4" style={{ backgroundColor: "oklch(0.97 0.005 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
                  <h3 className="text-xs uppercase tracking-widest font-bold mb-5" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Quick facts: RATBV public transport in Brașov
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: "Single ticket price", value: "5 RON (~€1)", note: "10 RON card = 2 trips. Validated on board." },
                      { label: "Where to buy", value: "Card, app, or kiosk", note: "24Pay app (iOS/Android), RATBV kiosks, or directly from driver on some routes" },
                      { label: "Operating hours", value: "~05:30–23:00", note: "Varies by route. Night buses on some lines." },
                      { label: "Official schedules", value: "ratbv.ro", note: "Always verify times before travelling — schedules change seasonally" },
                      { label: "Ticket validation", value: "Mandatory on board", note: "Validate immediately after boarding. Inspectors check regularly." },
                      { label: "Google Maps", value: "Works for live times", note: "Real-time departures available for most RATBV routes" },
                    ].map((fact, i) => (
                      <div key={i} className="p-4 border border-stone-100" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                        <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">{fact.label}</p>
                        <p className="font-bold text-stone-900 text-sm leading-tight mb-1" style={{ fontFamily: "Playfair Display, serif" }}>{fact.value}</p>
                        <p className="text-xs text-stone-500 font-light">{fact.note}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone-400 mt-4 font-light">
                    Prices and schedules are subject to change.{" "}
                    <a
                      href="https://www.ratbv.ro/tarife/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-dotted underline-offset-2 hover:text-stone-600 transition-colors"
                    >
                      Verify current fares on ratbv.ro
                    </a>{" "}
                    before travelling.
                  </p>
                </div>

                {/* Table of Contents */}
                <div className="my-8 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>In this guide</p>
                  <ol className="space-y-2">
                    {[
                      { n: "01", label: "Linia 24 — the city's backbone route", anchor: "#linia-24" },
                      { n: "02", label: "Linia 20 — Poiana Brașov without a car", anchor: "#linia-20" },
                      { n: "03", label: "Linia 55 — the local secret to Cetățuie", anchor: "#linia-55" },
                      { n: "04", label: "Linia 50 — to Pietrele lui Solomon", anchor: "#linia-50" },
                      { n: "05", label: "Linia 100 — straight from the train station", anchor: "#linia-100" },
                      { n: "06", label: "How to buy a ticket and not get fined", anchor: "#practical" },
                    ].map((item) => (
                      <li key={item.n} className="flex items-start gap-3">
                        <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }}>{item.n}</span>
                        <a href={item.anchor} className="text-stone-600 text-sm font-light hover:text-stone-900 transition-colors no-underline" style={{ lineHeight: "1.5" }}>
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Section 1 — Linia 24 */}
                <h2 id="linia-24" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Linia 24 — the city's backbone route
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  If you only take one bus in Brașov, make it the 24. It runs from Livada Poștei — the main transport hub in the city centre — all the way to Stupini and Baciului in the east, covering 27 stops in about an hour. That is the full width of the city, end to end, for one ticket.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The first stretch runs along <strong>Strada Lungă</strong> — one of the oldest streets in Brașov, documented since the 13th century and once the main access route from the north into the medieval citadel. At 2,420 metres, it is one of the longest streets in the old city, and the buildings along it reflect several centuries of architectural history: Baroque facades, 19th-century merchant houses, and the occasional reminder that this street was renamed Strada Voroshilov during the Soviet period before reverting to its original name in the 1960s.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Further along, the route passes the <strong>Transilvania University research centre</strong> (UNITBV), then continues through Bartolomeu — one of the city's older industrial and residential districts — before reaching the quieter eastern outskirts of Stupini and Baciului, where newer residential developments have been expanding in recent years. If you are considering{" "}
                  <a href="https://brasovgateway.com/buying-apartment-foreigner-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    buying an apartment in Brașov
                  </a>
                  , this route gives you a useful ground-level view of where the new residential developments are actually located.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Frequency is good — buses run every 10 to 15 minutes during peak hours. The 24 is also the line most likely to be crowded during morning and evening rush hours, which tells you something about how central it is to daily life here.
                </p>

                {/* Route summary box */}
                <div className="my-6 p-5 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: "oklch(0.38 0.12 15)" }}>Route 24 at a glance</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "From", value: "Livada Poștei" },
                      { label: "To", value: "Stupini / Baciului" },
                      { label: "Stops", value: "~27" },
                      { label: "Journey time", value: "~60 min" },
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">{item.label}</p>
                        <p className="font-semibold text-stone-800 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone-400 mt-4 font-light">
                    <a
                      href="https://www.ratbv.ro/linia-24/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-dotted underline-offset-2 hover:text-stone-600 transition-colors"
                    >
                      Full schedule and stops on ratbv.ro →
                    </a>
                  </p>
                </div>

                {/* Section 2 — Linia 20 */}
                <h2 id="linia-20" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Linia 20 — Poiana Brașov without a car
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Poiana Brașov is Romania's most famous ski resort, and it sits 12 kilometres from the city centre at around 1,020 metres altitude — roughly 620 metres above the city itself. Most guides will tell you to take a taxi or rent a car. The guides are wrong — or at least incomplete. Linia 20 runs from Livada Poștei directly to Poiana Brașov, and it does so year-round, not just in ski season.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  In winter, the 20 fills up with skiers and snowboarders heading up for the day. In summer, it is hikers going to the trailheads around Poiana — the routes up to Vârful Postăvarul and the ridge trails are all accessible from the resort without a car. The bus climbs steadily through the forest above the city, and by the time you reach Poiana, the temperature is noticeably cooler than down in the valley.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Frequency is lower than the 24 — expect buses every 30 to 45 minutes depending on the season. Check the schedule in advance, especially for the return journey, because the last bus back to the city runs earlier than you might expect. Missing it means a taxi, which is a different budget conversation entirely.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  One detail worth knowing for the return trip: one of the stops on the way back down is located in a parking area that offers an open panoramic view over the old town and the Carpathian ridge behind it. It is a genuinely good photo spot — the kind that works at any time of day, and especially well in the late afternoon when the light hits the rooftops. If you are not in a hurry, get off there, take the photos, and then walk the rest of the way down through the forest into the city centre. The descent takes about 45 minutes and is an easy trail — a proper hike with a natural finish right in the old town.
                </p>

                <div className="my-6 p-5 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: "oklch(0.38 0.12 15)" }}>Route 20 at a glance</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "From", value: "Livada Poștei" },
                      { label: "To", value: "Poiana Brașov" },
                      { label: "Altitude gain", value: "~620 m above city" },
                      { label: "Journey time", value: "~30 min" },
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">{item.label}</p>
                        <p className="font-semibold text-stone-800 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone-400 mt-4 font-light">
                    <a
                      href="https://www.ratbv.ro/linia-20/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-dotted underline-offset-2 hover:text-stone-600 transition-colors"
                    >
                      Full schedule and stops on ratbv.ro →
                    </a>
                  </p>
                </div>

                {/* Section 3 — Linia 55 */}
                <h2 id="linia-55" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Linia 55 — the local secret to Cetățuie
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Cetățuia is the fortress on the hill north of the old town — the one you can see from Piața Sfatului if you look up past the rooftops. Most visitors who make it up there walk, which takes about 20 minutes on a steep path through the woods. What almost nobody knows is that there is a bus that goes there.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Linia 55 is a short route — just a handful of stops — connecting Livada Poștei to the Cetățuie area. It does not run all day, and the schedule is limited, so this is one where checking{" "}
                  <a
                    href="https://www.ratbv.ro/linia-55/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors"
                    style={{ color: "oklch(0.38 0.12 15)" }}
                  >
                    the timetable on ratbv.ro
                  </a>{" "}
                  before you go is not optional — it is necessary. But if the timing works, you get the panoramic view over Brașov and the Carpathians for the price of a single ticket, without the climb.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The view from Cetățuia is one of the best in the city. The old town below, the mountains behind it, the red rooftops — it is the kind of thing that ends up on postcards. The difference is that you are looking at it from above, with almost no other tourists around, because almost no other tourists know the bus exists.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  After the visit, the walk back down into the city is easy — going downhill is a different experience entirely, and the path through the old town gives you a second chance to notice things you walked past on the way up. It is a natural end to a half-day that started with a 5-lei bus ticket.
                </p>

                <div className="my-6 p-5 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: "oklch(0.38 0.12 15)" }}>Route 55 at a glance</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "From", value: "Livada Poștei" },
                      { label: "To", value: "Cetățuie" },
                      { label: "Stops", value: "4" },
                      { label: "Note", value: "Limited schedule" },
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">{item.label}</p>
                        <p className="font-semibold text-stone-800 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone-400 mt-4 font-light">
                    <a
                      href="https://www.ratbv.ro/linia-55/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-dotted underline-offset-2 hover:text-stone-600 transition-colors"
                    >
                      Full schedule and stops on ratbv.ro →
                    </a>
                  </p>
                </div>

                {/* Section 4 — Linia 50 */}
                <h2 id="linia-50" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Linia 50 — to Pietrele lui Solomon
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Pietrele lui Solomon — Solomon's Rocks — is a limestone outcrop on the eastern edge of the city, popular with local climbers and hikers but largely unknown to visitors. The trail up takes about 30 minutes from the bus stop, and the reward is a rocky viewpoint with a different angle on the city than the more famous spots offer.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Linia 50 runs from Camera de Comerț — the Chamber of Commerce stop in the city centre — out to the Pietrele lui Solomon area. It is a less frequent route, so planning around the schedule matters. This is a good option if you have already done Tâmpa and Cetățuie and want a third viewpoint that feels genuinely off the tourist track.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The area around Pietrele lui Solomon also has a small lake and some picnic spots, which makes it a reasonable half-day destination if the weather cooperates. Bring water and reasonable footwear — the path is rocky in places.
                </p>

                <div className="my-6 p-5 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: "oklch(0.38 0.12 15)" }}>Route 50 at a glance</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "From", value: "Camera de Comerț" },
                      { label: "To", value: "Pietrele lui Solomon" },
                      { label: "Best for", value: "Hiking / viewpoint" },
                      { label: "Note", value: "Check schedule" },
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">{item.label}</p>
                        <p className="font-semibold text-stone-800 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone-400 mt-4 font-light">
                    <a
                      href="https://www.ratbv.ro/linia-50/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-dotted underline-offset-2 hover:text-stone-600 transition-colors"
                    >
                      Full schedule and stops on ratbv.ro →
                    </a>
                  </p>
                </div>

                {/* Section 5 — Linia 100 */}
                <h2 id="linia-100" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Linia 100 — straight from the train station to Poiana
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  If you are arriving in Brașov by train and want to go straight to Poiana Brașov — ski gear, luggage and all — Linia 100 is the answer. It runs directly from Gara Brașov (the main train station) to the Telecabina in Poiana Brașov, without requiring a transfer in the city centre.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The 100 is a seasonal and limited-frequency route — it does not run as often as the 20, and the timetable is tighter. It is worth checking{" "}
                  <a
                    href="https://www.ratbv.ro/linia-100/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors"
                    style={{ color: "oklch(0.38 0.12 15)" }}
                  >
                    the current schedule on ratbv.ro
                  </a>{" "}
                  before you plan your arrival around it. But when the timing works, it saves you the hassle of navigating the city with bags, and it costs the same 5 lei as every other trip.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  For visitors who are coming specifically for skiing or hiking in Poiana and have no other reason to spend time in the city centre, this route is genuinely useful. It is also a good option for the return journey — train to Bucharest, bus from Poiana to the station, no taxi needed.
                </p>

                <div className="my-6 p-5 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: "oklch(0.38 0.12 15)" }}>Route 100 at a glance</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "From", value: "Gara Brașov" },
                      { label: "To", value: "Telecabina Poiana" },
                      { label: "Best for", value: "Arrivals by train" },
                      { label: "Note", value: "Seasonal / limited" },
                    ].map((item, i) => (
                      <div key={i}>
                        <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">{item.label}</p>
                        <p className="font-semibold text-stone-800 text-sm" style={{ fontFamily: "Playfair Display, serif" }}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone-400 mt-4 font-light">
                    <a
                      href="https://www.ratbv.ro/linia-100/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline decoration-dotted underline-offset-2 hover:text-stone-600 transition-colors"
                    >
                      Full schedule and stops on ratbv.ro →
                    </a>
                  </p>
                </div>

                {/* Section 6 — Practical */}
                <h2 id="practical" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  How to buy a ticket and not get fined
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The RATBV ticketing system is straightforward once you understand it, but it has one rule that catches a lot of visitors: you must validate your ticket immediately after boarding. Not at the stop, not before you sit down — on the machine inside the bus, as soon as you get on. Inspectors board regularly, and "I forgot" is not a defence that works.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  There are three ways to pay. The easiest for visitors is the contactless card reader on board — tap your bank card or phone and the fare is deducted automatically. No ticket, no app, no change needed. The second option is the{" "}
                  <strong>24Pay app</strong> (available on iOS and Android), which lets you buy and validate tickets digitally. The third is a physical ticket from a RATBV kiosk or vending machine at major stops — these are 10 RON cards loaded with 2 trips.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  For schedules, Google Maps works well for most RATBV routes and shows real-time departures. For routes with limited frequency — especially the 55, 50, and 100 — cross-reference with the{" "}
                  <a
                    href="https://www.ratbv.ro"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors"
                    style={{ color: "oklch(0.38 0.12 15)" }}
                  >
                    official RATBV website
                  </a>{" "}
                  before you leave. Schedules change seasonally, and an outdated timetable on a third-party site has left more than a few people waiting at a stop for a bus that no longer runs at that time.
                </p>

                <blockquote className="border-l-4 pl-6 my-8 italic text-stone-600 font-light" style={{ borderLeftColor: "oklch(0.38 0.12 15)", lineHeight: "1.8" }}>
                  "Five lei gets you across the city, up to a ski resort, or to a fortress with a panoramic view. The bus is not a compromise — it is just the option that most visitors never consider."
                </blockquote>

                {/* Routes comparison table */}
                <h2 className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  All five routes at a glance
                </h2>
                <div className="my-6 w-full overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
                  <table className="border-collapse" style={{ width: "100%", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}>
                    <thead>
                      <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        {["Route", "From → To", "Best for", "Frequency", "Schedule"].map((h) => (
                          <th key={h} className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "0.6rem 0.8rem", fontSize: "0.7rem", letterSpacing: "0.05em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { route: "24", from: "Livada Poștei → Stupini", best: "Seeing the whole city", freq: "Every 10–15 min", link: "https://www.ratbv.ro/linia-24/" },
                        { route: "20", from: "Livada Poștei → Poiana Brașov", best: "Skiing / hiking", freq: "Every 30–45 min", link: "https://www.ratbv.ro/linia-20/" },
                        { route: "55", from: "Livada Poștei → Cetățuie", best: "Panoramic view", freq: "Limited", link: "https://www.ratbv.ro/linia-55/" },
                        { route: "50", from: "Camera de Comerț → Pietrele lui Solomon", best: "Hiking / climbing", freq: "Limited", link: "https://www.ratbv.ro/linia-50/" },
                        { route: "100", from: "Gara Brașov → Telecabina Poiana", best: "Arrivals by train", freq: "Seasonal / limited", link: "https://www.ratbv.ro/linia-100/" },
                      ].map((row, i) => (
                        <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "oklch(0.985 0.006 75)" : "oklch(0.975 0.005 75)" }}>
                          <td className="text-stone-700 font-bold border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>{row.route}</td>
                          <td className="text-stone-700 border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>{row.from}</td>
                          <td className="text-stone-600 font-light border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>{row.best}</td>
                          <td className="text-stone-500 border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>{row.freq}</td>
                          <td className="border-b border-stone-100" style={{ padding: "0.6rem 0.8rem" }}>
                            <a href={row.link} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors text-xs" style={{ color: "oklch(0.38 0.12 15)" }}>
                              ratbv.ro →
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* CTA box */}
                <div className="mt-14 p-8 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
                    Thinking about spending more than a few days in Brașov?
                  </h3>
                  <p className="text-stone-600 font-light leading-relaxed mb-5" style={{ lineHeight: "1.7" }}>
                    The bus is a good start. If you are evaluating Brașov as a place to live — or invest — the numbers are worth looking at properly. We have put together{" "}
                    <Link href="/what-to-do-in-brasov-city-guide" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                      a complete guide to what the city has to offer
                    </Link>
                    , and{" "}
                    <Link href="/cost-of-living-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                      the 2026 cost of living numbers
                    </Link>{" "}
                    for anyone doing the maths on a longer stay.
                  </p>
                  <Link href="/contact">
                    <button
                      className="text-xs text-white px-6 py-3 transition-opacity hover:opacity-90"
                      style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
                    >
                      GET IN TOUCH
                    </button>
                  </Link>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-8 mb-4">
                  {["Brașov", "City Guide", "Transport", "Expat life", "Tourism", "Practical"].map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 border border-stone-200 text-stone-500 font-light">
                      {tag}
                    </span>
                  ))}
                </div>

                <FeaturedDeveloper />
              </article>
            </main>

            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="sticky top-24 flex flex-col gap-6">
                <SidebarAd />
                <div className="p-5 border border-stone-200 bg-white">
                  <h4 className="font-bold text-stone-900 mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    About Brașov Gateway
                  </h4>
                  <p className="text-stone-500 text-sm font-light leading-relaxed mb-4">
                    We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support — all in one place.
                  </p>
                  <a
                    href="/#contact"
                    className="block text-center text-xs text-white py-2.5 transition-opacity hover:opacity-90 no-underline"
                    style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
                  >
                    GET IN TOUCH
                  </a>
                </div>
                <div className="mt-6 p-5" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
                  <h4 className="font-bold text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                    Stay in the loop
                  </h4>
                  <p className="text-stone-300 text-xs font-light leading-relaxed mb-4">
                    No spam. One email per week with what is actually happening in Brașov.
                  </p>
                  <NewsletterInlineLight />
                </div>
                <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                  <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Real estate services</p>
                  <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                    Looking for an apartment in Brașov?
                  </h4>
                  <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                    We help foreigners find, evaluate, and buy property in Brașov — without the guesswork.
                  </p>
                  <Link href="/services/real-estate">
                    <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                      VIEW SERVICE
                    </button>
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {/* Related articles */}
          <div className="mt-20 pt-12 border-t border-stone-200">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-8" style={{ backgroundColor: "oklch(0.38 0.12 15)" }} />
              <span className="text-xs uppercase tracking-widest text-stone-400">More from the blog</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {RELATED.map((post, i) => (
                <Link key={i} href={post.href} className="group cursor-pointer no-underline block">
                  <div className="overflow-hidden mb-4">
                    <img src={post.img} alt={post.title} className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "oklch(0.38 0.12 15)" }}>
                    {post.tag}
                  </span>
                  <h3 className="mt-2 text-base font-semibold text-stone-900 group-hover:text-stone-600 transition-colors leading-snug" style={{ fontFamily: "Playfair Display, serif" }}>
                    {post.title}
                  </h3>
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold" style={{ color: "oklch(0.38 0.12 15)", letterSpacing: "0.08em" }}>
                    READ MORE <ChevronRight size={11} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <SiteFooter />
      </div>
    </>
  );
}
